import { useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoriesAPI } from '../../services/api';
import { InventoryListResponse, InventorySummary } from '../../types/inventory';

/**
 * Query hooks for Inventory (server-paginated list + summary).
 *
 * Key root MUST be 'inventories' — the slug the realtime-invalidation
 * middleware broadcasts for /api/inventories — so a direct inventory change
 * auto-refreshes this view. Like the other list hooks, no staleTime is set:
 * cached data shows instantly on navigation while a background refetch keeps it
 * current (inventory is derived from stock movements, so it must stay fresh).
 */
export interface InventoriesListParams {
  skip: number;
  limit: number;
  search?: string;
  articleId?: number;
  extra?: Record<string, any>;
}

export const inventoriesKeys = {
  root: ['inventories'] as const,
  list: (p: InventoriesListParams) => ['inventories', 'list', p] as const,
  summary: ['inventories', 'summary'] as const,
};

export function useInventoriesList(params: InventoriesListParams, enabled = true) {
  return useQuery<InventoryListResponse>({
    queryKey: inventoriesKeys.list(params),
    queryFn: () =>
      inventoriesAPI.getPaginated(
        params.skip,
        params.limit,
        params.search,
        params.articleId,
        undefined,
        params.extra,
      ),
    enabled,
    keepPreviousData: true,
  });
}

export function useInventorySummary(enabled = true) {
  return useQuery<InventorySummary>({
    queryKey: inventoriesKeys.summary,
    queryFn: () => inventoriesAPI.getSummary(),
    enabled,
  });
}

export function useInvalidateInventories() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: inventoriesKeys.root });
}
